import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useRange } from "react-instantsearch";

const toTimestampStartOfDay = (dateStr) => {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
};

const toTimestampEndOfDay = (dateStr) => {
    const d = new Date(dateStr);
    d.setHours(23, 59, 59, 999);
    return Math.floor(d.getTime() / 1000);
};

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("fr-FR");
};

const timestampToInputDate = (ts) => {
    const d = new Date(ts * 1000);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const CustomDateFilter = ({ attribute }) => {
    const { start, refine, range } = useRange({ attribute });

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [activeShortcut, setActiveShortcut] = useState(null);
    const debouncedTimer = useRef(null);

    useEffect(() => {
        if (!Number.isFinite(start[0]) && !Number.isFinite(start[1])) {
            setStartDate("");
            setEndDate("");
            setActiveShortcut(null);
        }
    }, [start]);

    const applyFilter = (sDate, eDate) => {
        if (sDate && sDate.length < 10) return;
        if (eDate && eDate.length < 10) return;

        let minTs = sDate ? toTimestampStartOfDay(sDate) : undefined;
        let maxTs = eDate ? toTimestampEndOfDay(eDate) : undefined;

        if (range.min && minTs && minTs < range.min) minTs = range.min;
        if (range.max && maxTs && maxTs > range.max) maxTs = range.max;
        if (minTs && maxTs && minTs > maxTs) return;

        refine([minTs, maxTs]);
    };

    const handleStartChange = (value) => {
        setStartDate(value);
        setActiveShortcut(null);
        if (debouncedTimer.current) clearTimeout(debouncedTimer.current);
        debouncedTimer.current = setTimeout(() => applyFilter(value, endDate), 500);
    };

    const handleEndChange = (value) => {
        setEndDate(value);
        setActiveShortcut(null);
        if (debouncedTimer.current) clearTimeout(debouncedTimer.current);
        debouncedTimer.current = setTimeout(() => applyFilter(startDate, value), 500);
    };

    const handleShortcut = (days) => {
        if (debouncedTimer.current) clearTimeout(debouncedTimer.current);

        if (activeShortcut === days) {
            setActiveShortcut(null);
            setStartDate("");
            setEndDate("");
            refine([undefined, undefined]);
            return;
        }

        const now = new Date();
        const from = new Date();
        from.setDate(now.getDate() - days);
        from.setHours(0, 0, 0, 0);
        now.setHours(23, 59, 59, 999);

        const fromTs = Math.floor(from.getTime() / 1000);
        const toTs = Math.floor(now.getTime() / 1000);

        const safeMin = (range.min && fromTs < range.min) ? range.min : fromTs;
        const safeMax = (range.max && toTs > range.max) ? range.max : toTs;

        setStartDate(timestampToInputDate(safeMin));
        setEndDate(timestampToInputDate(safeMax));
        setActiveShortcut(days);
        refine([safeMin, safeMax]);
    };

    const hasFilter = startDate || endDate;

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setActiveShortcut(null);
        refine([undefined, undefined]);
    };

    return (
        <div className="crl-panel">
            {hasFilter && (
                <p className="date-summary">
                    {startDate && `Du ${formatDate(startDate)}`}
                    {startDate && endDate && ' '}
                    {endDate && `au ${formatDate(endDate)}`}
                </p>
            )}
            {/* Raccourcis */}
            <div className="date-shortcuts">
                {[7, 30, 365].map((d) => (
                    <button
                        key={d}
                        type="button"
                        className={`fr-tag fr-tag-date ${activeShortcut === d ? 'fr-tag--active' : ''}`}
                        aria-pressed={activeShortcut === d}
                        onClick={() => handleShortcut(d)}
                    >
                        {d === 7 ? "Moins d’une semaine" : d === 30 ? "Moins d’un mois" : "Moins d’un an"}
                    </button>
                ))}
            </div>

            {/* Inputs date */}
            <div className="date-inputs">
                <div className="date-field">
                    <label className="date-label">Du</label>
                    <input
                        className="date-input"
                        type="date"
                        value={startDate}
                        onChange={(e) => handleStartChange(e.target.value)}
                    />
                </div>
                <div className="date-field">
                    <label className="date-label">Au</label>
                    <input
                        className="date-input"
                        type="date"
                        value={endDate}
                        onChange={(e) => handleEndChange(e.target.value)}
                    />
                </div>
            </div>

            {/* Résumé de la sélection */}


            {/* Tout effacer */}
            {hasFilter && (
                <div className="crl-actions">
                    <button className="crl-action-btn crl-clear" onClick={handleClear}>
                        Tout effacer
                    </button>
                </div>
            )}
        </div>
    );
};

CustomDateFilter.propTypes = {
    attribute: PropTypes.string.isRequired,
};

export default CustomDateFilter;